import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiTimeGrain,
  BiTimeRange,
  BiTimeRangeKind,
  kBiMaxRollingCount,
} from '@euler/model/bi/query';
import { memo, useEffect, useState } from 'react';
import { View } from 'react-native';
import { BiChip } from '../../components/ui/BiChip';
import { RangeTextInput } from './RangeTextInput';
import { biRollingInput } from './functions/biRangeInput';

// Chip labels; a range's own wording (`3 个月`) is kBiRollingUnitLabels.
const kUnits: Array<{ value: BiTimeGrain; label: string }> = [
  { value: BiTimeGrain.Day, label: '天' },
  { value: BiTimeGrain.Week, label: '周' },
  { value: BiTimeGrain.Month, label: '月' },
  { value: BiTimeGrain.Quarter, label: '季度' },
  { value: BiTimeGrain.Year, label: '年' },
];

/**
 * "最近 N 天/周/月/季度/年", calendar-aligned and re-resolved on every
 * run; 含本期 counts the current unit as one of the N.
 */
export const RollingRangeFields = memo(
  ({
    value,
    onPick,
  }: {
    value: BiTimeRange | undefined;
    onPick: (range: BiTimeRange) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const rolling = value?.kind === BiTimeRangeKind.Rolling ? value : null;
    const [count, setCount] = useState(String(rolling?.count ?? 3));
    const [unit, setUnit] = useState(rolling?.unit ?? BiTimeGrain.Month);
    const [current, setCurrent] = useState(rolling?.includeCurrent ?? true);
    // A range set from outside (another card opened) shows as typed.
    // Any other range resets the fields, so nothing typed for another card
    // is sent with this one.
    useEffect(() => {
      setCount(String(rolling?.count ?? 3));
      setUnit(rolling?.unit ?? BiTimeGrain.Month);
      setCurrent(rolling?.includeCurrent ?? true);
    }, [rolling?.count, rolling?.unit, rolling?.includeCurrent]); // eslint-disable-line react-hooks/exhaustive-deps
    const input = biRollingInput(count, unit, current);
    const apply = (next: {
      count?: string;
      unit?: BiTimeGrain;
      current?: boolean;
    }) => {
      const typed = biRollingInput(
        next.count ?? count,
        next.unit ?? unit,
        next.current ?? current,
      );
      if (typed.kind === 'ok') onPick(typed.range);
    };
    return (
      <View style={{ gap: rpx(6) }}>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: rpx(6),
          }}
        >
          <Typography.Text
            size={rpx(12)}
            color={rolling ? aiColors.primary : aiColors.foreground}
          >
            最近
          </Typography.Text>
          <RangeTextInput
            label="最近几个单位"
            value={count}
            width={56}
            numeric
            onChange={setCount}
            // Sent once typed, not at every keystroke ("120" is not 1, 12).
            onCommit={() => apply({})}
          />
          {kUnits.map(option => (
            <BiChip
              key={option.value}
              label={option.label}
              selected={rolling?.unit === option.value}
              onPress={() => {
                // A count past the new unit's cap comes down to it.
                const max = kBiMaxRollingCount[option.value];
                const capped = Number(count) > max ? String(max) : count;
                setUnit(option.value);
                setCount(capped);
                apply({ unit: option.value, count: capped });
              }}
            />
          ))}
          <BiChip
            label="含本期"
            selected={!!rolling && current}
            onPress={() => {
              setCurrent(!current);
              apply({ current: !current });
            }}
          />
        </View>
        {input.kind === 'invalid' ? (
          <Typography.Text size={rpx(11)} color={aiColors.destructive}>
            {input.problem}
          </Typography.Text>
        ) : null}
      </View>
    );
  },
);
